import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Gauge, Power, Route, ArrowRight, Car, X } from "lucide-react";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const COLORS = {
  tersedia: "#198754",
  digunakan: "#10056E",
  penyelenggaraan: "#0284c7",
};
const LABELS = {
  tersedia: "Tersedia",
  digunakan: "Digunakan",
  penyelenggaraan: "Penyelenggaraan",
};
const BADGE_CLS = {
  tersedia: "bg-[#198754] hover:bg-[#198754]",
  digunakan: "bg-[#10056E] hover:bg-[#10056E]",
  penyelenggaraan: "bg-sky-600 hover:bg-sky-600",
};

function FlyTo({ target }) {
  const map = useMap();
  if (target) map.flyTo([target.lat, target.lng], 13, { duration: 0.8 });
  return null;
}

export default function FleetMap({ positions }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return positions.filter((p) => `${p.registration_number} ${p.make} ${p.model}`.toLowerCase().includes(q)).slice(0, 8);
  }, [query, positions]);

  const counts = useMemo(() => {
    const c = { tersedia: 0, digunakan: 0, penyelenggaraan: 0 };
    positions.forEach((p) => { if (c[p.status] !== undefined) c[p.status] += 1; });
    return c;
  }, [positions]);

  const pick = (p) => { setSelected(p); setQuery(""); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 rounded-lg overflow-hidden border border-slate-200" data-testid="fleet-map">
      <div className="lg:col-span-3 relative">
        <MapContainer center={[3.139, 101.687]} zoom={11} scrollWheelZoom={false} style={{ height: 440, width: "100%", zIndex: 0 }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FlyTo target={selected} />
          {positions.map((p) => (
            <CircleMarker
              key={p.vehicle_id}
              center={[p.lat, p.lng]}
              radius={selected?.vehicle_id === p.vehicle_id ? 13 : 9}
              eventHandlers={{ click: () => pick(p) }}
              pathOptions={{
                color: selected?.vehicle_id === p.vehicle_id ? "#EEBD0A" : "#ffffff",
                weight: selected?.vehicle_id === p.vehicle_id ? 3.5 : 2,
                fillColor: COLORS[p.status] || "#64748b",
                fillOpacity: 1,
              }}
            />
          ))}
        </MapContainer>
        <div className="absolute bottom-3 left-3 z-[500] bg-white/95 border border-slate-200 rounded-lg px-3 py-2 flex gap-4 text-xs shadow-sm">
          {Object.entries(LABELS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: COLORS[k] }} />
              {v} <b>{counts[k]}</b>
            </span>
          ))}
        </div>
      </div>

      <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white flex flex-col" data-testid="fleet-map-panel">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari kenderaan..." value={query}
              onChange={(e) => setQuery(e.target.value)} data-testid="vehicle-search-input" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 388 }}>
          {query.trim() ? (
            <div data-testid="vehicle-search-results">
              {results.length === 0 && <p className="text-xs text-muted-foreground p-4">Tiada kenderaan sepadan.</p>}
              {results.map((p) => (
                <button key={p.vehicle_id} onClick={() => pick(p)} data-testid={`vehicle-result-${p.registration_number}`}
                  className="w-full text-left px-4 py-2.5 border-b hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-bold">{p.registration_number}</p>
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: COLORS[p.status] || "#64748b" }} />
                  </div>
                  <p className="text-[11px] text-muted-foreground">{p.make} {p.model}{p.category ? ` · ${p.category}` : ""}</p>
                </button>
              ))}
            </div>
          ) : selected ? (
            <div className="p-4 space-y-3" data-testid="vehicle-info-box">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-lg font-bold leading-tight">{selected.registration_number}</p>
                  <p className="text-xs text-muted-foreground">{selected.make} {selected.model}{selected.category ? ` · ${selected.category}` : ""}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600" data-testid="vehicle-info-close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <Badge className={BADGE_CLS[selected.status] || "bg-slate-500"} data-testid="vehicle-info-status">
                {LABELS[selected.status] || selected.status}
              </Badge>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2 text-xs text-muted-foreground"><Gauge className="h-3.5 w-3.5" /> Kelajuan</span>
                  <b className="text-sm">{selected.speed_kmh} km/j</b>
                </div>
                <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2 text-xs text-muted-foreground"><Power className="h-3.5 w-3.5" /> Enjin</span>
                  <b className={`text-sm ${selected.ignition ? "text-[#198754]" : "text-slate-500"}`}>{selected.ignition ? "Hidup" : "Mati"}</b>
                </div>
                <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2 text-xs text-muted-foreground"><Route className="h-3.5 w-3.5" /> Odometer</span>
                  <b className="text-sm">{Number(selected.odometer).toLocaleString()} km</b>
                </div>
              </div>
              <Button size="sm" className="w-full bg-[#10056E] hover:bg-[#2a17a8] h-9" data-testid="vehicle-info-detail-btn"
                onClick={() => navigate(`/kenderaan/${selected.vehicle_id}`)}>
                Lihat Detail Kenderaan <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          ) : (
            <div className="p-4 text-center space-y-3" data-testid="vehicle-info-empty">
              <div className="h-12 w-12 rounded-full bg-indigo-50 text-[#10056E] flex items-center justify-center mx-auto mt-4">
                <Car className="h-6 w-6" />
              </div>
              <p className="text-sm font-semibold">Pilih Kenderaan</p>
              <p className="text-xs text-muted-foreground px-2">Klik penanda pada peta atau cari nombor plat untuk melihat maklumat kenderaan.</p>
              <div className="text-left space-y-1.5 pt-2">
                {Object.entries(LABELS).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs border rounded-lg px-3 py-2">
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[k] }} /> {v}</span>
                    <b>{counts[k]}</b>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
